
import { Moon, Sun } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useTheme } from "@/components/theme-provider";
import { useHydrationSafe } from "@/hooks/useHydrationSafe";
import { cn } from "@/lib/utils";

interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const { isHydrated } = useHydrationSafe();
  const { theme, setTheme } = useTheme();
  
  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  // 🛡️ Pre-hydration: render static placeholder matching SSR default (light theme)
  // CRITICAL: NO Radix components here - use pure HTML to avoid hydration mismatch #418
  if (!isHydrated) {
    return (
      <div className={cn("flex items-center space-x-2 p-1 rounded-full bg-muted/50 dark:bg-gray-800 border border-border/50 shadow-sm transition-all duration-300", className)}>
        <Sun className="h-4 w-4 ml-1 text-gold" />
        {/* Static switch placeholder - replicates Radix Switch visually without Radix internals */}
        <div 
          className="peer inline-flex h-6 w-11 shrink-0 items-center rounded-full border-2 border-transparent bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-50"
          role="switch"
          aria-checked="false"
          aria-disabled="true"
          aria-label="Cambia tema"
        >
          <span className="pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 translate-x-0" />
        </div>
        <Moon className="h-4 w-4 mr-1 text-gray-400" />
      </div>
    );
  }

  return (
    <div className={cn("flex items-center space-x-2 p-1 rounded-full bg-muted/50 dark:bg-gray-800 border border-border/50 shadow-sm transition-all duration-300", className)}>
      <Sun className={cn("h-4 w-4 ml-1", theme === "light" ? "text-gold" : "text-gray-400")} />
      <Switch
        checked={theme === "dark"}
        onCheckedChange={toggleTheme}
        aria-label="Cambia tema"
      />
      <Moon className={cn("h-4 w-4 mr-1", theme === "dark" ? "text-white" : "text-gray-400")} />
    </div>
  );
}
